import type { AreaUsageItem, AreaUsagesResponse } from 'types/area-usages';

/** The routed record an area geometry backs — a Unit's development area. */
const DEVELOPMENT_AREA = 'development_area';

/**
 * The development area a just-created area geometry backs.
 *
 * `POST /area_geometries` answers with the `area_geometries.id`, but the whole
 * builder is keyed on the `development_areas.id` of the unit that geometry backs
 * — a separate record the create view never serialises. The usages query resolves
 * exactly that: its tier-1 entry is
 * `DevelopmentArea.where(area_geometry_id: ids).pluck(:id, :name)`.
 *
 * Deliberately not read off the development-areas listing. That endpoint answers
 * "which units belong to this workspace, formation and viewport", so it can
 * legitimately omit a brand-new unit — outside the current map bounds, centroid
 * outside the origin type-curve area, or excluded for the formation. This lookup
 * is keyed on the geometry alone and so is unaffected by any of them.
 *
 * @param {AreaUsagesResponse} response - The usages query's answer.
 * @return {AreaUsageItem | null} The unit's `{ id, label }`, or null when the
 *   routed record is not reported.
 */
export const developmentAreaFromUsages = (
  response: AreaUsagesResponse
): AreaUsageItem | null => {
  const tier1 = response.results?.tier1 ?? [];
  const routed = tier1.find((group) => group.type === DEVELOPMENT_AREA);
  return routed?.items?.[0] ?? null;
};
